import { z } from 'zod';

// ============================================================
// ENUM SCHEMAS
// ============================================================

/**
 * Alle erlaubten Block-Typen im CMS-Baukasten-System.
 * Enthält alle 7 About-kompatiblen Typen plus 8 neue CMS-Typen
 * für Rückwärtskompatibilität bei der About-Migration in Phase 5.
 */
export const CmsBlockTypeSchema = z.enum([
  // Bestehende About-Typen (Phase 5 Migration)
  'text',
  'gallery',
  'contact',
  'combined',
  'video',
  'quote',
  'faq',
  // Neue CMS-Typen
  'hero',
  'stats',
  'cta',
  'documents',
  'event-list',
  'social-links',
  'persons',
  'blog-post',
  'accordion',
  'carousel',
]);

/**
 * Erlaubte Seiten-Typen für CmsPage.
 * Bestimmt das Layout und die verfügbaren Block-Typen einer Seite.
 */
export const CmsPageTypeSchema = z.enum(['standard', 'activity', 'about', 'landing']);

/** Label-Map für CMS-Seitentypen (Dashboard-Anzeige). */
export const CMS_PAGE_TYPE_LABELS: Record<z.infer<typeof CmsPageTypeSchema>, string> = {
  standard: 'Standard',
  activity: 'Aktivität',
  about: 'Über uns',
  landing: 'Landing',
};

/**
 * Erlaubte Navigations-Eintrags-Typen für CmsNavItem.
 */
export const CmsNavItemTypeSchema = z.enum(['link', 'group', 'external']);

// ============================================================
// BLOCK-CONTENT-SCHEMAS (neue CMS-Typen)
// ============================================================

/**
 * Inhalt eines Hero-Blocks.
 * Volldimensionaler Seiten-Header mit optionalem Hintergrundbild und CTA-Button.
 */
export const HeroBlockContentSchema = z.object({
  headline: z.string().min(1).max(200),
  subheadline: z.string().max(300).optional(),
  eyebrow: z.string().max(200).optional(),
  backgroundText: z.string().max(50).optional(),
  /** MediaFile-ID des Hintergrundbilds */
  mediaFileId: z.string().optional(),
  backgroundMediaFileId: z.string().optional(),
  ctaLabel: z.string().max(100).nullable().optional(),
  ctaUrl: z.string().nullable().optional(),
  ctaType: z.enum(['internal', 'external']).default('internal').optional(),
});

/**
 * Inhalt eines Stats-Blocks.
 * Zeigt Kennzahlen/Statistiken als Kacheln an (z.B. "Gründungsjahr: 2024").
 */
export const StatsBlockContentSchema = z.object({
  items: z
    .array(
      z.object({
        label: z.string().min(1).max(100),
        /** Wert als String — z.B. "2024", "150+", "4" */
        value: z.string().min(1).max(50),
        /** Lucide Icon-Name (optional) */
        icon: z.string().optional(),
      }),
    )
    .min(1)
    .max(8),
});

/**
 * Inhalt eines CTA-Blocks (Call to Action).
 * Hervorgehobener Bereich mit Überschrift, Text und bis zu zwei Buttons.
 */
export const CtaBlockContentSchema = z.object({
  heading: z.string().min(1).max(200),
  /** Fließtext als Markdown (optional) */
  body: z.string().max(2000).optional(),
  /** Label des Primär-Buttons */
  primaryLabel: z.string().min(1).max(100),
  /** URL des Primär-Buttons — intern beginnt mit /, extern mit https?:// */
  primaryUrl: z
    .string()
    .min(1)
    .max(500)
    .refine((v) => v.startsWith('/') || /^https?:\/\//i.test(v), {
      message: 'primaryUrl muss ein interner Pfad (/) oder eine https?://-URL sein',
    }),
  /** Interner oder externer Link */
  primaryType: z.enum(['internal', 'external']).default('internal').optional(),
  /** Label des optionalen Sekundär-Buttons */
  secondaryLabel: z.string().max(100).optional(),
  /** URL des optionalen Sekundär-Buttons — intern beginnt mit /, extern mit https?:// */
  secondaryUrl: z
    .string()
    .max(500)
    .refine((v) => v.startsWith('/') || /^https?:\/\//i.test(v), {
      message: 'secondaryUrl muss ein interner Pfad (/) oder eine https?://-URL sein',
    })
    .optional(),
  /** Interner oder externer Link */
  secondaryType: z.enum(['internal', 'external']).default('internal').optional(),
  variant: z.enum(['default', 'discord', 'highlight']).default('default').optional(),
});

/**
 * Inhalt eines Dokumente-Blocks.
 * Liste von herunterladbaren Dokumenten (PDF-Dateien aus dem Media-System).
 */
export const DocumentsBlockContentSchema = z.object({
  heading: z.string().max(200).optional(),
  items: z
    .array(
      z.object({
        label: z.string().min(1).max(200),
        /** MediaFile-ID des PDFs */
        mediaFileId: z.string(),
        description: z.string().max(500).optional(),
      }),
    )
    .min(1)
    .max(20),
});

/**
 * Inhalt eines Event-Listen-Blocks.
 * Zeigt gefilterte Events aus der Datenbank an.
 */
export const EventListBlockContentSchema = z.object({
  heading: z.string().max(200).optional(),
  /** Filtert nach EventCategory.slug */
  categorySlug: z.string().optional(),
  mode: z.enum(['upcoming', 'past', 'all']).default('upcoming'),
  maxItems: z.number().int().min(1).max(20).default(6).optional(),
});

/**
 * Inhalt eines Social-Links-Blocks.
 * Zeigt Social-Media-Verlinkungen an.
 */
export const SocialLinksBlockContentSchema = z.object({
  heading: z.string().max(200).optional(),
  items: z
    .array(
      z.object({
        platform: z.enum(['discord', 'instagram', 'twitter', 'youtube', 'twitch', 'other']),
        label: z.string().min(1).max(100),
        url: z.string().url(),
      }),
    )
    .min(1)
    .max(10),
});

/**
 * Inhalt eines Personen-Blocks.
 * Zeigt Team-Mitglieder oder Ansprechpartner als Kacheln oder Liste an.
 */
export const PersonsBlockContentSchema = z.object({
  heading: z.string().max(200).optional(),
  layout: z.enum(['grid', 'list']).default('grid').optional(),
  persons: z
    .array(
      z.object({
        name: z.string().min(1).max(200),
        role: z.string().max(200).optional(),
        bio: z.string().max(1000).optional(),
        /** MediaFile-ID des Profilbilds */
        avatarMediaFileId: z.string().optional(),
        email: z.string().email().optional(),
        socialUrl: z.string().optional(),
      }),
    )
    .min(1)
    .max(20),
});

/**
 * Inhalt eines Blog-Post-Blocks.
 * Einzelner Beitrag mit Titel, Autor, Datum und Markdown-Body.
 */
export const BlogPostBlockContentSchema = z.object({
  title: z.string().min(1).max(200),
  /** ISO-Date-String (optional) */
  date: z.string().optional(),
  author: z.string().max(200).optional(),
  /** MediaFile-ID des Cover-Bilds */
  coverMediaFileId: z.string().optional(),
  /** Beitragstext als Markdown */
  body: z.string().min(1).max(50000),
  tags: z.array(z.string().max(50)).max(10).optional(),
});

/**
 * Einzelnes Element in einem Accordion-Block.
 */
export const AccordionItemSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(200),
  content: z.string().min(1).max(5000),
});

/**
 * Inhalt eines Accordion-Blocks.
 * Zeigt ausklappbare Frage-Antwort-Elemente oder strukturierte Inhalte an.
 */
export const AccordionBlockContentSchema = z.object({
  heading: z.string().max(200).optional(),
  items: z.array(AccordionItemSchema).min(1).max(20),
});

/**
 * Einzelne Folie in einem Karussell-Block.
 */
export const CarouselSlideSchema = z.object({
  id: z.string(),
  title: z.string().max(200).optional(),
  content: z.string().min(1).max(5000),
  /** URL des optionalen Folien-Bilds */
  imageUrl: z.string().url().optional(),
});

/**
 * Inhalt eines Karussell-Blocks.
 * Zeigt eine Folge von Folien mit optionalem Autoplay und Dot-Navigation an.
 */
export const CarouselBlockContentSchema = z.object({
  slides: z.array(CarouselSlideSchema).min(1).max(20),
  autoplay: z.boolean().default(false),
  /** Autoplay-Intervall in Millisekunden (2000–10000), Standard 5000. */
  interval: z.number().min(2000).max(10000).default(5000).optional(),
});

// ============================================================
// CMS PAGE SCHEMAS
// ============================================================

/**
 * CMS-Seite — entspricht dem CmsPage Prisma-Model.
 */
export const CmsPageSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  pageType: CmsPageTypeSchema,
  isPublished: z.boolean(),
  sortOrder: z.number().int(),
  metaTitle: z.string().nullable().optional(),
  metaDescription: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Request-Body zum Erstellen einer neuen CMS-Seite.
 */
export const CreateCmsPageSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten'),
  title: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
  pageType: CmsPageTypeSchema.optional().default('standard'),
  isPublished: z.boolean().optional().default(false),
  sortOrder: z.number().int().min(0).optional().default(0),
  metaTitle: z.string().max(200).optional(),
  metaDescription: z.string().max(500).optional(),
});

/**
 * Request-Body zum Aktualisieren einer CMS-Seite (alle Felder optional).
 */
export const UpdateCmsPageSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten')
    .optional(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(500).nullable().optional(),
  pageType: CmsPageTypeSchema.optional(),
  isPublished: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  metaTitle: z.string().max(200).nullable().optional(),
  metaDescription: z.string().max(500).nullable().optional(),
});

// ============================================================
// CMS SECTION SCHEMAS
// ============================================================

/**
 * CMS-Section — entspricht dem CmsSection Prisma-Model.
 */
export const CmsSectionSchema = z.object({
  id: z.string(),
  pageId: z.string(),
  name: z.string(),
  slug: z.string(),
  sortOrder: z.number().int(),
  isVisible: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Request-Body zum Erstellen einer neuen CMS-Section.
 */
export const CreateCmsSectionSchema = z.object({
  name: z.string().min(1).max(200),
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten'),
  sortOrder: z.number().int().min(0).optional().default(0),
  isVisible: z.boolean().optional().default(true),
});

/**
 * Request-Body zum Aktualisieren einer CMS-Section (alle Felder optional).
 */
export const UpdateCmsSectionSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten')
    .optional(),
  sortOrder: z.number().int().min(0).optional(),
  isVisible: z.boolean().optional(),
});

// ============================================================
// CMS BLOCK SCHEMAS
// ============================================================

/**
 * CMS-Block — entspricht dem CmsBlock Prisma-Model.
 * Das Feld `content` ist ein raw JSON-String — der Client parsed ihn mit dem typ-spezifischen Schema.
 */
export const CmsBlockSchema = z.object({
  id: z.string(),
  sectionId: z.string(),
  type: CmsBlockTypeSchema,
  /** Raw JSON-String — parsed mit dem passenden Block-Content-Schema */
  content: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  lastEditedById: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Request-Body zum Erstellen eines neuen CMS-Blocks.
 * Content wird als JSON-String übergeben und serverseitig validiert.
 */
export const CreateCmsBlockSchema = z.object({
  type: CmsBlockTypeSchema,
  /** JSON-String des typ-spezifischen Block-Contents */
  content: z.string().min(1),
  sortOrder: z.number().int().min(0).optional().default(0),
  isActive: z.boolean().optional().default(true),
});

/**
 * Request-Body zum Aktualisieren eines CMS-Blocks (alle Felder optional).
 */
export const UpdateCmsBlockSchema = z.object({
  type: CmsBlockTypeSchema.optional(),
  content: z.string().min(1).optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
});

// ============================================================
// CMS NAV ITEM SCHEMAS
// ============================================================

/**
 * CMS-Navigations-Eintrag — entspricht dem CmsNavItem Prisma-Model.
 */
export const CmsNavItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  url: z.string().nullable().optional(),
  pageSlug: z.string().nullable().optional(),
  type: CmsNavItemTypeSchema,
  parentId: z.string().nullable().optional(),
  sortOrder: z.number().int(),
  isVisible: z.boolean(),
  openInNewTab: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * CMS-Navigations-Eintrag mit verschachtelten Kinder-Einträgen (für Dropdown-Gruppen).
 */
export const CmsNavItemTreeSchema: z.ZodType<CmsNavItemTree> = z.lazy(() =>
  CmsNavItemSchema.extend({
    children: z.array(CmsNavItemTreeSchema).optional(),
  }),
);

/**
 * Request-Body zum Erstellen eines neuen Navigations-Eintrags.
 */
export const CreateCmsNavItemSchema = z.object({
  label: z.string().min(1).max(200),
  url: z.string().optional(),
  pageSlug: z.string().optional(),
  type: CmsNavItemTypeSchema.optional().default('link'),
  parentId: z.string().nullable().optional(),
  sortOrder: z.number().int().min(0).optional().default(0),
  isVisible: z.boolean().optional().default(true),
  openInNewTab: z.boolean().optional().default(false),
});

/**
 * Request-Body zum Aktualisieren eines Navigations-Eintrags (alle Felder optional).
 */
export const UpdateCmsNavItemSchema = z.object({
  label: z.string().min(1).max(200).optional(),
  url: z.string().nullable().optional(),
  pageSlug: z.string().nullable().optional(),
  type: CmsNavItemTypeSchema.optional(),
  parentId: z.string().nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
  isVisible: z.boolean().optional(),
  openInNewTab: z.boolean().optional(),
});

// ============================================================
// UTILITY SCHEMAS
// ============================================================

/**
 * Request-Body zum Neu-Sortieren von CMS-Entitäten (Pages, Sections, Blocks, NavItems).
 * Identisch zum AboutReorderSchema-Pattern.
 */
export const CmsReorderSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string(),
        sortOrder: z.number().int().min(0),
      }),
    )
    .min(1),
});

// ============================================================
// TYPESCRIPT TYPES
// ============================================================

/** Block-Typ-Enum */
export type CmsBlockType = z.infer<typeof CmsBlockTypeSchema>;

/** Seiten-Typ-Enum */
export type CmsPageType = z.infer<typeof CmsPageTypeSchema>;

/** NavItem-Typ-Enum */
export type CmsNavItemType = z.infer<typeof CmsNavItemTypeSchema>;

/** CMS-Seite */
export type CmsPage = z.infer<typeof CmsPageSchema>;

/** CMS-Section */
export type CmsSection = z.infer<typeof CmsSectionSchema>;

/** CMS-Block */
export type CmsBlock = z.infer<typeof CmsBlockSchema>;

/** CMS-Navigations-Eintrag */
export type CmsNavItem = z.infer<typeof CmsNavItemSchema>;

/** CMS-Navigations-Eintrag mit Kinder-Einträgen */
export interface CmsNavItemTree extends z.infer<typeof CmsNavItemSchema> {
  children?: CmsNavItemTree[];
}

/** Request-Typen */
export type CreateCmsPage = z.infer<typeof CreateCmsPageSchema>;
export type UpdateCmsPage = z.infer<typeof UpdateCmsPageSchema>;
export type CreateCmsSection = z.infer<typeof CreateCmsSectionSchema>;
export type UpdateCmsSection = z.infer<typeof UpdateCmsSectionSchema>;
export type CreateCmsBlock = z.infer<typeof CreateCmsBlockSchema>;
export type UpdateCmsBlock = z.infer<typeof UpdateCmsBlockSchema>;
export type CreateCmsNavItem = z.infer<typeof CreateCmsNavItemSchema>;
export type UpdateCmsNavItem = z.infer<typeof UpdateCmsNavItemSchema>;
export type CmsReorder = z.infer<typeof CmsReorderSchema>;

/** Typed Block-Content-Typen */
export type HeroBlockContent = z.infer<typeof HeroBlockContentSchema>;
export type StatsBlockContent = z.infer<typeof StatsBlockContentSchema>;
export type CtaBlockContent = z.infer<typeof CtaBlockContentSchema>;
export type DocumentsBlockContent = z.infer<typeof DocumentsBlockContentSchema>;
export type EventListBlockContent = z.infer<typeof EventListBlockContentSchema>;
export type SocialLinksBlockContent = z.infer<typeof SocialLinksBlockContentSchema>;
export type PersonsBlockContent = z.infer<typeof PersonsBlockContentSchema>;
export type BlogPostBlockContent = z.infer<typeof BlogPostBlockContentSchema>;
/** Einzelnes Accordion-Item */
export type AccordionItem = z.infer<typeof AccordionItemSchema>;
/** Inhalt eines Accordion-Blocks */
export type AccordionBlockContent = z.infer<typeof AccordionBlockContentSchema>;
/** Einzelne Folie eines Karussell-Blocks */
export type CarouselSlide = z.infer<typeof CarouselSlideSchema>;
/** Inhalt eines Karussell-Blocks */
export type CarouselBlockContent = z.infer<typeof CarouselBlockContentSchema>;

// ============================================================
// RESPONSE INTERFACES
// ============================================================

/**
 * Verschachtelter Section-Typ mit enthaltenen Blocks — für öffentliche API-Responses.
 */
export type CmsSectionWithBlocks = CmsSection & { blocks: CmsBlock[] };

/**
 * Verschachtelter Seiten-Typ mit enthaltenen Sections und Blocks.
 */
export type CmsPageWithContent = CmsPage & { sections: CmsSectionWithBlocks[] };

/**
 * Öffentliche API-Response für GET /api/public/cms/pages/:slug.
 * Enthält die vollständige Seite mit allen sichtbaren Sections und aktiven Blocks.
 */
export interface PublicCmsPageResponse {
  page: CmsPageWithContent;
}

/**
 * Öffentliche API-Response für GET /api/public/cms/navigation.
 * Enthält die Navigationsstruktur als Baum mit verschachtelten Kinder-Einträgen.
 */
/** Öffentliche API-Response für GET /api/public/cms/pages (Liste). */
export interface PublicCmsPagesResponse {
  pages: CmsPage[];
}

export interface PublicCmsNavigationResponse {
  items: CmsNavItemTree[];
}

/** Alias für Navigation-Response (ohne "Public" Prefix). */
export type CmsNavigationResponse = PublicCmsNavigationResponse;
